/**
 * Construction Order Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as constructionService from '../api/services/construction.service';
import type {
  ConstructionOrderCreateRequest,
  ConstructionOrderUpdateRequest,
  ConstructionOrderDetail,
  ConstructionPhoto,
} from '../types';
import { toast } from '../stores/toast.store';

// Query keys
const KEYS = {
  orders: ['construction', 'orders'] as const,
  order: (id: number) => ['construction', 'orders', id] as const,
  extras: ['construction', 'extras'] as const,
  states: ['construction', 'states'] as const,
  teams: ['construction', 'teams'] as const,
  photos: (orderId: number) => ['construction', 'orders', orderId, 'photos'] as const,
};

interface UseConstructionOrdersParams {
  offset?: number;
  limit?: number;
  state?: string;
  saleOrderId?: number;
  search?: string;
  myOrders?: boolean;
}

/**
 * Hook for fetching construction orders list
 */
export function useConstructionOrders(params: UseConstructionOrdersParams = {}) {
  return useQuery({
    queryKey: [...KEYS.orders, params],
    queryFn: async () => {
      const result = await constructionService.getConstructionOrders({
        offset: params.offset,
        limit: params.limit,
        state: params.state,
        sale_order_id: params.saleOrderId,
        search: params.search,
        my_orders: params.myOrders,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch orders');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching single construction order
 */
export function useConstructionOrder(orderId: number | undefined) {
  return useQuery<ConstructionOrderDetail>({
    queryKey: KEYS.order(orderId!),
    queryFn: async () => {
      const result = await constructionService.getConstructionOrder(orderId!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch order');
      }
      return result.data!;
    },
    enabled: !!orderId,
  });
}

/**
 * Hook for fetching construction extras
 */
export function useConstructionExtras() {
  return useQuery({
    queryKey: KEYS.extras,
    queryFn: async () => {
      const result = await constructionService.getExtras();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch extras');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching construction states
 */
export function useConstructionStates() {
  return useQuery({
    queryKey: KEYS.states,
    queryFn: async () => {
      const result = await constructionService.getStates();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch states');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching construction teams
 */
export function useConstructionTeams() {
  return useQuery({
    queryKey: KEYS.teams,
    queryFn: async () => {
      const result = await constructionService.getTeams();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch teams');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for creating construction order
 */
export function useCreateConstructionOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ConstructionOrderCreateRequest) => {
      const result = await constructionService.createOrder(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create order');
      }
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.orders });
      toast.success('施工單建立成功');
    },
    onError: (error: Error) => {
      toast.error('建立失敗', error.message);
    },
  });
}

/**
 * Hook for updating construction order
 */
export function useUpdateConstructionOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      data,
    }: {
      orderId: number;
      data: ConstructionOrderUpdateRequest;
    }) => {
      const result = await constructionService.updateOrder(orderId, data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to update order');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.orders });
      queryClient.setQueryData(KEYS.order(data.id), data);
      toast.success('施工單更新成功');
    },
    onError: (error: Error) => {
      toast.error('更新失敗', error.message);
    },
  });
}

/**
 * Hook for fetching construction order photos
 */
export function useConstructionPhotos(orderId: number | undefined) {
  return useQuery<ConstructionPhoto[]>({
    queryKey: KEYS.photos(orderId!),
    queryFn: async () => {
      const result = await constructionService.getPhotos(orderId!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch photos');
      }
      return result.data!;
    },
    enabled: !!orderId,
  });
}

/**
 * Hook for uploading construction order photos
 */
export function useUploadConstructionPhotos() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      images,
    }: {
      orderId: number;
      images: string[];
    }) => {
      const result = await constructionService.uploadPhotos(orderId, images);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to upload photos');
      }
      return { orderId, photos: result.data! };
    },
    onSuccess: ({ orderId }) => {
      queryClient.invalidateQueries({ queryKey: KEYS.photos(orderId) });
      toast.success('照片上傳成功');
    },
    onError: (error: Error) => {
      toast.error('上傳失敗', error.message);
    },
  });
}

/**
 * Hook for deleting construction order photo
 */
export function useDeleteConstructionPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      photoId,
    }: {
      orderId: number;
      photoId: number;
    }) => {
      const result = await constructionService.deletePhoto(orderId, photoId);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete photo');
      }
      return { orderId, photoId };
    },
    onSuccess: ({ orderId }) => {
      queryClient.invalidateQueries({ queryKey: KEYS.photos(orderId) });
      toast.success('照片已刪除');
    },
    onError: (error: Error) => {
      toast.error('刪除失敗', error.message);
    },
  });
}
